/**
 * Decision 0128 addendum (2026-10-20): a teammate's chat turn waits on a login (a CLI login on its keeper's machine, or
 * a personal subscription on the Platform Runner) that its owner has not shared. The hub names the login in the run's
 * waiting state (`lendable` with `profileId`, core `lendCandidatesFor`); this module turns that into one question card
 * for the login's owner per login and asker: "Share your Claude login in Dictly so Becky's message can run?"
 * with Share in Dictly (`ShareLogin{inProject}`, applied as the owner by the project's outbox, which adds this project to
 * the login's projects: decision 0166 §3.2) and Not now.
 *
 * The card goes away by itself: `settleShareCards` withdraws it once no waiting turn of that asker names that login
 * (the scope changed, the turn ran or ended). A card answered Not now is not asked again for the turns that were
 * waiting then; a later message asks again.
 */
import type { AskWait, LendCandidate } from "@minga/protocol"
import { deriveId } from "../hash"
import { isWorkingMember, projectAdminsOf } from "../multiplayer"
import { openProposal } from "../runs"
import { agentOf, isOpenProposal, memberOf } from "../state"
import type { Actor, Command, Ids, OptionEffect, ProjectState, Proposal, Run } from "../types"
import { forbidden, invalid, type Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

type UserId = Ids["UserId"]

/** The process that opens and withdraws share cards (it places runs; it never creates items). */
export const SHARE_CARD_ACTOR: Actor = { _tag: "system", process: "scheduler" }

const PREFIX = "share_login:"

/** One card per login (runner and profile) and asker. */
export const shareIntentKey = (runnerId: string, profileId: string, askerId: string): string =>
  `${PREFIX}${runnerId}:${profileId}:${askerId}`

export const isShareCard = (p: Proposal): boolean => p.intentKey.startsWith(PREFIX)

/** The person who asked for a turn, when that is not its keeper (`Run.askedBy`); else null. */
export const turnAskerOf = (run: Run): UserId | null =>
  run.kind === "turn" && run.askedBy?._tag === "user" && run.askedBy.userId !== run.ownerId
    ? run.askedBy.userId
    : null

/** The share candidates of a waiting turn asked by someone other than its keeper. */
const shareCandidatesOf = (run: Run): ReadonlyArray<LendCandidate & { readonly profileId: string }> =>
  run.state._tag === "waiting" && turnAskerOf(run) !== null
    ? (run.state.lendable ?? []).filter((c): c is LendCandidate & { profileId: Ids["ProfileId"] } =>
        Boolean(c.profileId),
      )
    : []

/** Decision 0166 §2: the card for a project admin when no one's login could run an asker's turn. */
export const adminShareIntentKey = (askerId: string, adminId: string): string =>
  `${PREFIX}none:${askerId}:${adminId}`

/** What says a turn waits on a login, not on a machine. */
const ACCESS_WAIT = new Set(["owner_lock", "missing_agent", "profile_not_ready", "profile_out_of_scope"])
/** What says it waits for a machine (asleep, paused, busy, rate limited): a share would not help now. */
const MACHINE_WAIT = new Set([
  "no_runner_online",
  "runner_offline",
  "runner_paused",
  "at_capacity",
  "profile_at_capacity",
  "rate_limited",
  "exclusive_held",
])

/**
 * Decision 0166 §2: a teammate's turn waits because no login they may use runs it and nobody's personal login would
 * either (no share candidate): the project's admins are asked to connect or share one. Not when the asker's own choice
 * of subscriptions in this project left out one that would run it (§3.6 `not_chosen`): that is theirs to change.
 */
const waitsWithoutLogin = (run: Run): boolean =>
  run.state._tag === "waiting" &&
  turnAskerOf(run) !== null &&
  noLoginWouldRun(shareCandidatesOf(run), run.state.unmet)

const noLoginWouldRun = (
  candidates: ReadonlyArray<LendCandidate>,
  unmet: ReadonlyArray<{ readonly _tag: string }>,
): boolean =>
  candidates.length === 0 &&
  unmet.some((u) => ACCESS_WAIT.has(u._tag)) &&
  !unmet.some((u) => MACHINE_WAIT.has(u._tag) || u._tag === "not_chosen")

/** Re-walk 0166 §2: a queued message's share candidates (`AskWait.lendable` with a profile). */
const askCandidatesOf = (w: AskWait): ReadonlyArray<LendCandidate & { readonly profileId: string }> =>
  (w.lendable ?? []).filter((c): c is LendCandidate & { profileId: Ids["ProfileId"] } => Boolean(c.profileId))

/** Re-walk 0166 §2: whose message a share card is for (the asker), from its intent key. */
const askerOfShareKey = (key: string): string | null => {
  const parts = key.slice(PREFIX.length).split(":")
  return parts[0] === "none" ? (parts[1] ?? null) : (parts.at(-1) ?? null)
}

/** The intent keys some waiting turn still backs. */
const wantedKeys = (s: ProjectState): ReadonlySet<string> => {
  const keys = new Set<string>()
  for (const run of s.runs) {
    const asker = turnAskerOf(run)
    if (asker === null) continue
    for (const c of shareCandidatesOf(run)) keys.add(shareIntentKey(c.runnerId, c.profileId, asker))
    if (waitsWithoutLogin(run))
      for (const admin of projectAdminsOf(s)) keys.add(adminShareIntentKey(asker, admin))
  }
  // re-walk 0166 §2: a message waiting behind another turn backs its cards until its own turn is requested
  for (const w of s.askWaits ?? []) {
    for (const c of askCandidatesOf(w)) keys.add(shareIntentKey(c.runnerId, c.profileId, w.askerId))
    if (noLoginWouldRun(askCandidatesOf(w), w.unmet))
      for (const admin of projectAdminsOf(s)) keys.add(adminShareIntentKey(w.askerId, admin))
  }
  return keys
}

/**
 * Re-walk 0166 §2: the askers whose turn to an agent was just requested and has not said yet what it waits on: their
 * open cards for that agent stay until it does (the card a queued message raised is not withdrawn and raised again).
 */
const requestedAskers = (s: ProjectState): ReadonlySet<string> => {
  const out = new Set<string>()
  for (const run of s.runs) {
    const asker = turnAskerOf(run)
    if (asker !== null && run.state._tag === "requested") out.add(`${run.agentId}:${asker}`)
  }
  return out
}

/** "Claude", "Codex": the CLI's name for the card. */
export const loginName = (agent: string | undefined): string =>
  agent === "codex" ? "Codex" : agent === "antigravity" ? "Antigravity" : agent === "muse" ? "Muse" : "Claude"

/**
 * Open the share cards a waiting turn needs (called after its `RunWaiting`). Skipped when one is open for the same login
 * and asker, when its owner is not a working member of this project (they could not see the card), and when the owner
 * answered one for this login and asker since this turn was asked (Not now holds for the turns waiting then).
 */
export const openShareCards = (tx: Tx, run: Run): void => {
  const asker = turnAskerOf(run)
  if (asker === null) return
  openCardsFor(tx, {
    asker,
    agentId: run.agentId ?? null,
    since: run.requestedAt,
    seed: run.id,
    candidates: shareCandidatesOf(run),
    noLogin: waitsWithoutLogin(run),
  })
}

/**
 * Re-walk 0166 §2: a message that waits behind another turn (`RecordAskWaiting`): the scheduler probed its own turn
 * with its asker; record what it would wait on and open the same cards a waiting turn opens, now.
 */
export const recordAskWaiting = (tx: Tx, c: Cmd<"RecordAskWaiting">): void => {
  const agent = agentOf(tx.state, c.agentId)
  if (!agent) invalid("no such agent", "agentId")
  // the keeper's own turns never wait on someone else's login
  if (agent?.ownerId === c.askerId) return
  const lendable = (c.lendable ?? []).filter((x) => x.profileId !== undefined)
  const before = tx.state.askWaits?.find((x) => x.agentId === c.agentId && x.askerId === c.askerId)
  // nothing new: the same reasons (or still none) were recorded already
  if (
    (before === undefined && c.unmet.length === 0) ||
    (before !== undefined &&
      JSON.stringify([before.unmet, before.lendable ?? []]) === JSON.stringify([c.unmet, lendable]))
  )
    return
  tx.emit({
    _tag: "AskWaiting",
    agentId: c.agentId,
    askerId: c.askerId,
    unmet: c.unmet,
    since: c.since,
    ...(lendable.length > 0 ? { lendable } : {}),
  })
  const w = tx.state.askWaits?.find((x) => x.agentId === c.agentId && x.askerId === c.askerId)
  if (!w) return
  openCardsFor(tx, {
    asker: c.askerId,
    agentId: c.agentId,
    since: c.since,
    seed: `ask:${c.agentId}:${c.since}`,
    candidates: askCandidatesOf(w),
    noLogin: noLoginWouldRun(askCandidatesOf(w), w.unmet),
  })
}

const openCardsFor = (
  tx: Tx,
  ask: {
    readonly asker: UserId
    readonly agentId: Ids["AgentId"] | null
    /** When the message was asked: a card answered since is not asked again for it. */
    readonly since: number
    readonly seed: string
    readonly candidates: ReadonlyArray<LendCandidate & { readonly profileId: string }>
    readonly noLogin: boolean
  },
): void => {
  const { asker } = ask
  const run = { id: ask.seed, agentId: ask.agentId, requestedAt: ask.since }
  for (const c of ask.candidates) {
    const s = tx.state
    const key = shareIntentKey(c.runnerId, c.profileId, asker)
    if (c.ownerId === asker || !isWorkingMember(s, c.ownerId) || memberOf(s, c.ownerId) === undefined)
      continue
    const same = s.proposals.filter((p) => p.intentKey === key)
    if (same.some(isOpenProposal)) continue
    if (same.some((p) => p.state === "answered" && (p.resolution?.at ?? 0) >= run.requestedAt)) continue
    const askerName = memberOf(s, asker)?.displayName ?? "a teammate"
    const agentName = run.agentId ? (agentOf(s, run.agentId)?.name ?? "an agent") : "an agent"
    const login = loginName(c.agent)
    const effect: OptionEffect = {
      _tag: "ShareLogin",
      runnerId: c.runnerId,
      profileId: c.profileId as Ids["ProfileId"],
      ownerId: c.ownerId,
      ...(c.agent ? { agent: c.agent } : {}),
      inProject: true,
    }
    // decision 0166 §3.2: shared in this project only, not with the whole workspace
    const project = s.project?.name || "this project"
    const prompt = `Share your ${login} login in ${project} so ${askerName}'s message can run?`
    const reasoning = `${askerName}'s message to ${agentName} waits: your ${login} login does not run others' work in ${project}. Shared in ${project}, it runs anyone's work there, paid by your plan. You can change where it is shared in Settings › Subscriptions.`
    tx.withScope({ actor: SHARE_CARD_ACTOR }, () =>
      openProposal(tx, {
        id: deriveId<Ids["ProposalId"]>("prop", tx.now, `${key}:${run.id}:${tx.state.seq}`),
        ownerId: c.ownerId,
        body: {
          _tag: "question",
          prompt,
          subject: run.agentId ? { _tag: "agent", id: run.agentId } : null,
          options: [
            {
              key: "share",
              label: `Share in ${project}`,
              detail: `${askerName}'s message to ${agentName} runs now. Your ${login} login then runs anyone's work in ${project}, paid by your plan; Settings › Subscriptions changes it again.`,
              effects: [effect],
            },
            {
              key: "not_now",
              label: "Not now",
              detail: `${askerName}'s message keeps waiting until a login it may use is shared in ${project}.`,
              effects: [],
            },
          ],
          allowFreeText: false,
          defaultOnExpiry: null,
          answersAsk: null,
          escalation: null,
        },
        proposedBy: SHARE_CARD_ACTOR,
        reasoning,
        oneLine: `${askerName}'s message to ${agentName} waits on your ${login} login`,
        risk: "low",
        reversibility: "reversible",
        tier: "soon",
        blocks: [],
        tainted: false,
        approver: { _tag: "human", userId: c.ownerId },
        decideBy: null,
        expiresAt: null,
        intentKey: key,
      }),
    )
  }
  if (ask.noLogin) openAdminCards(tx, run, asker)
}

/**
 * Decision 0166 §2: nobody's login could run the turn, so each project admin (not the asker) is asked to connect a
 * subscription or share one. The answer changes nothing by itself; the card goes once the turn runs or stops waiting
 * on a login (`settleShareCards`). Not now holds for the turns waiting then, as for a share card.
 */
const openAdminCards = (
  tx: Tx,
  run: { readonly id: string; readonly agentId: Ids["AgentId"] | null; readonly requestedAt: number },
  asker: UserId,
): void => {
  const s0 = tx.state
  const askerName = memberOf(s0, asker)?.displayName ?? "a teammate"
  const agent = run.agentId ? agentOf(s0, run.agentId) : undefined
  const agentName = agent?.name ?? "an agent"
  const login = agent?.mind._tag === "runner" ? loginName(agent.mind.agent) : "Claude or Codex"
  // decision 0166 §3.2: a subscription shared in this project is enough, as on the owner's share card
  const project = s0.project?.name || "this project"
  for (const admin of projectAdminsOf(s0)) {
    if (admin === asker) continue
    const s = tx.state
    const key = adminShareIntentKey(asker, admin)
    const same = s.proposals.filter((p) => p.intentKey === key)
    if (same.some(isOpenProposal)) continue
    if (same.some((p) => p.state === "answered" && (p.resolution?.at ?? 0) >= run.requestedAt)) continue
    tx.withScope({ actor: SHARE_CARD_ACTOR }, () =>
      openProposal(tx, {
        id: deriveId<Ids["ProposalId"]>("prop", tx.now, `${key}:${run.id}:${tx.state.seq}`),
        ownerId: admin,
        body: {
          _tag: "question",
          prompt: `Connect or share a ${login} subscription in ${project} so ${askerName}'s message can run?`,
          subject: run.agentId ? { _tag: "agent", id: run.agentId } : null,
          options: [
            {
              key: "will_do",
              label: "I'll set one up",
              detail: `Connect a subscription in Settings › Model providers and share it in ${project} (Settings › Subscriptions), or ask ${askerName} to connect their own. ${askerName}'s message runs then.`,
              effects: [],
            },
            {
              key: "not_now",
              label: "Not now",
              detail: `${askerName}'s message keeps waiting until a subscription it may use is connected or shared in ${project}.`,
              effects: [],
            },
          ],
          allowFreeText: false,
          defaultOnExpiry: null,
          answersAsk: null,
          escalation: null,
        },
        proposedBy: SHARE_CARD_ACTOR,
        reasoning: `${askerName}'s message to ${agentName} waits: no ${login} subscription ${askerName} may use in ${project} is connected, and nobody's could run it if shared there.`,
        oneLine: `${askerName}'s message to ${agentName} waits for a ${login} subscription`,
        risk: "low",
        reversibility: "reversible",
        tier: "soon",
        blocks: [],
        tainted: false,
        approver: { _tag: "human", userId: admin },
        decideBy: null,
        expiresAt: null,
        intentKey: key,
      }),
    )
  }
}

/**
 * Withdraw the open share cards no waiting turn backs any more: the login was shared (or the owner made the machine
 * the team's), the turn ran, or it ended. Runs after every command; cheap when no share card is open.
 */
export const settleShareCards = (tx: Tx): void => {
  const open = tx.state.proposals.filter((p) => isOpenProposal(p) && isShareCard(p))
  if (open.length === 0) return
  const wanted = wantedKeys(tx.state)
  const requested = requestedAskers(tx.state)
  const subjectOf = (p: Proposal): string | null =>
    p.body._tag === "question" && p.body.subject?._tag === "agent" ? p.body.subject.id : null
  for (const p of open)
    if (!wanted.has(p.intentKey) && !requested.has(`${subjectOf(p)}:${askerOfShareKey(p.intentKey)}`))
      tx.withScope({ actor: SHARE_CARD_ACTOR }, () =>
        tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id }),
      )
}

/** Only the login's owner picks Share: anyone else's pick is refused and the card stays open. */
export const checkShareLoginPick = (tx: Tx, effects: ReadonlyArray<OptionEffect>): void => {
  for (const e of effects) {
    if (e._tag !== "ShareLogin") continue
    if (tx.actor._tag !== "user" || tx.actor.userId !== e.ownerId) {
      const owner = memberOf(tx.state, e.ownerId)?.displayName ?? "its owner"
      forbidden("not_owner", `only ${owner} shares their own login`)
    }
  }
}
